'use client';

import type {
  IconButtonProps,
  MenuItemProps,
  DividerProps,
  PopperPlacementType,
} from '@mui/material';
import {
  ClickAwayListener,
  Fade,
  Paper,
  Popper,
  Divider,
  IconButton,
  MenuItem,
  MenuList,
  Stack,
} from '@mui/material';
import classnames from 'classnames';
import { useCallback, useState } from 'react';
import type { ReactNode, SyntheticEvent } from 'react';

import { Icon } from '@/components/shared/icon';

import { GroupTitle } from './groupTitle';

import { AuthorizationTooltip } from '../tooltips/authorizationTooltip';

export interface OptionMenuItemType {
  id: string;
  groupHeader?: boolean;
  divider?: boolean;
  dividerProps?: DividerProps;
  icon?: ReactNode;
  text?: ReactNode;
  menuItemProps?: MenuItemProps;
  predicate?: () => boolean;
  isAuthorized?: boolean;
  isFeatureSupported?: boolean;
  subMenu?: OptionType[];
  disabledTooltip?: string;
}

export type OptionType = string | OptionMenuItemType;

interface OptionsMenuType {
  icon?: ReactNode;
  iconClassName?: string;
  options: OptionType[];
  placement?: PopperPlacementType;
  iconButtonProps?: IconButtonProps;
  onToggle?: (open: boolean) => void;
  onOptionSelected?: (option: OptionType) => void;
  Component?: React.ElementType;
  componentProps?: Record<string, unknown>;
  popperClassName?: string;
}

const InnerMenu = (
  props: OptionsMenuType & {
    open: boolean;
    placement: PopperPlacementType;
    closeMenu: () => void;
    anchorEl: HTMLElement | null;
    enablePropagation?: boolean;
  }
) => {
  const {
    onOptionSelected,
    closeMenu,
    open,
    options,
    anchorEl,
    placement,
    popperClassName,
  } = props;
  const [subMenuOpen, setSubMenuOpen] = useState<number>();
  const handleClose = useCallback(
    (event: Event | SyntheticEvent) => {
      if (anchorEl?.contains(event.target as HTMLElement)) {
        return;
      }
      setSubMenuOpen(undefined);
      closeMenu();
    },
    [anchorEl, closeMenu]
  );
  const handleSelect = useCallback(
    (option: OptionType, e: Event | SyntheticEvent) => {
      if (onOptionSelected) onOptionSelected(option);
      handleClose(e);
    },
    [onOptionSelected, handleClose]
  );

  const filteredOptions = options.filter((o) =>
    typeof o === 'object' && 'predicate' in o ? o.predicate?.() : true
  );

  return (
    <Popper
      open={open}
      anchorEl={anchorEl}
      placement={placement}
      transition
      disablePortal
      className={popperClassName}
      sx={{ zIndex: 5 }}
    >
      {({ TransitionProps }) => (
        <Fade {...TransitionProps}>
          <Paper
            sx={{
              boxShadow: 'var(--mui-customShadows-lg)',
            }}
          >
            <ClickAwayListener onClickAway={handleClose}>
              <MenuList
                autoFocusItem={open}
                onClick={
                  props.enablePropagation
                    ? undefined
                    : (e) => e.stopPropagation()
                }
              >
                {filteredOptions.map((option: OptionType, index: number) => {
                  if (typeof option === 'string') {
                    return (
                      <MenuItem
                        key={option}
                        data-testid={option}
                        sx={{ minWidth: '150px' }}
                        onClick={(e) => {
                          handleSelect(option, e);
                        }}
                      >
                        {option}
                      </MenuItem>
                    );
                  } else if ('divider' in option) {
                    const previousOption = filteredOptions[index - 1];
                    if (
                      index === 0 ||
                      (typeof previousOption !== 'string' &&
                        'divider' in previousOption)
                    ) {
                      return null;
                    }
                    if (!option.divider) return null;
                    return (
                      <Divider
                        key={option.id}
                        {...option.dividerProps}
                        className='my-[8px]'
                      />
                    );
                  } else if ('groupHeader' in option) {
                    return <GroupTitle key={option.id} title={option.text} />;
                  } else {
                    return (
                      <SubMenuInnerOption
                        key={option.id}
                        option={option}
                        subMenuOpen={subMenuOpen}
                        index={index}
                        setSubMenuOpen={setSubMenuOpen}
                        handleSelect={handleSelect}
                        props={props}
                      />
                    );
                  }
                })}
              </MenuList>
            </ClickAwayListener>
          </Paper>
        </Fade>
      )}
    </Popper>
  );
};

const SubMenuInnerOption = ({
  option,
  subMenuOpen,
  index,
  setSubMenuOpen,
  handleSelect,
  props,
}: {
  option: OptionMenuItemType;
  subMenuOpen: number | undefined;
  index: number;
  setSubMenuOpen: (index: number | undefined) => void;
  handleSelect: (option: OptionType, e: Event | SyntheticEvent) => void;
  props: OptionsMenuType & {
    open: boolean;
    placement: PopperPlacementType;
    closeMenu: () => void;
    anchorEl: HTMLElement | null;
    enablePropagation?: boolean;
  };
}) => {
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);

  return (
    <AuthorizationTooltip
      isAuthorized={option.isAuthorized}
      isFeatureSupported={option.isFeatureSupported}
      title={
        option.menuItemProps?.disabled
          ? option.disabledTooltip || undefined
          : undefined
      }
      key={option.id}
    >
      <MenuItem
        {...option.menuItemProps}
        ref={setAnchorEl}
        onMouseEnter={(e) => {
          setSubMenuOpen(index);
          option.menuItemProps?.onMouseEnter?.(e);
        }}
        onMouseLeave={(e) => {
          setSubMenuOpen(undefined);
          option.menuItemProps?.onMouseLeave?.(e);
        }}
        onClick={(e) => {
          handleSelect(option, e);
          option.menuItemProps?.onClick?.(e);
        }}
        data-testid={option.id}
      >
        <Option
          icon={option.icon}
          text={option.text}
          isSubMenu={!!option.subMenu}
        />
        {option.subMenu && (
          <InnerMenu
            {...props}
            anchorEl={anchorEl}
            options={option.subMenu}
            placement='right-start'
            open={subMenuOpen === index}
            closeMenu={() => setSubMenuOpen(undefined)}
            enablePropagation
          />
        )}
      </MenuItem>
    </AuthorizationTooltip>
  );
};

const OptionMenu = (props: OptionsMenuType) => {
  const {
    icon = 'material-symbols-more-horiz',
    iconClassName,
    iconButtonProps,
    onToggle,
    Component,
    componentProps,
    placement = 'bottom-end',
  } = props;
  const [open, setOpen] = useState(false);
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const handleToggle = (event: React.MouseEvent<HTMLButtonElement>) => {
    event.stopPropagation();
    setOpen((prevOpen) => !prevOpen);
    onToggle?.(!open);
  };

  if (
    props.options.length === 0 ||
    props.options.every(
      (obj) =>
        typeof obj === 'object' && 'predicate' in obj && !obj.predicate?.()
    )
  ) {
    return null;
  }

  return (
    <>
      {Component && (
        <Component
          {...componentProps}
          data-testid='option-menu-button'
          ref={setAnchorEl}
          onClick={handleToggle}
        />
      )}
      {!Component && (
        <IconButton
          disableRipple
          ref={setAnchorEl}
          size='small'
          data-testid='option-menu-button'
          onClick={handleToggle}
          {...iconButtonProps}
        >
          {typeof icon === 'string' ? (
            <i
              className={classnames(
                icon,
                'text-[var(--mui-palette-text-primary)]',
                iconClassName
              )}
            />
          ) : (
            icon
          )}
        </IconButton>
      )}
      <InnerMenu
        {...props}
        placement={placement}
        open={open}
        closeMenu={() => {
          setOpen(false);
          onToggle?.(false);
        }}
        anchorEl={anchorEl}
      />
    </>
  );
};

export const menuItemStyles = (isSelected: boolean) => {
  return {
    height: '36px',
    padding: '4px 16px 4px 16px',
    borderRadius: 0,

    ...(isSelected
      ? {
          backgroundColor: 'var(--mui-palette-primary-main)',
          color: 'var(--mui-palette-background-default)',
        }
      : {}),

    '&:hover': {
      backgroundColor: isSelected
        ? 'var(--mui-palette-primary-main)'
        : 'var(--mui-palette-primary-dark)',
    },
  };
};

export { OptionMenu };

const Option = ({
  icon,
  text,
  isSubMenu,
}: {
  icon?: ReactNode;
  text: ReactNode;
  isSubMenu?: boolean;
}) => {
  return (
    <Stack
      direction='row'
      alignItems='center'
      justifyContent='space-between'
      width='100%'
      gap='4px'
    >
      <Stack direction='row' alignItems='center' gap='8px' marginRight='8px'>
        {(typeof icon === 'string' ? (
          <Icon
            iconClass={icon}
            sx={{
              '& i': {
                color: 'currentColor',
              },
            }}
          />
        ) : (
          icon
        )) || null}
        {text}
      </Stack>
      {isSubMenu && (
        <Icon
          iconClass='material-symbols-chevron-right-rounded'
          sx={{
            padding: '0px',
            '& i': {
              margin: '0px',
              color: 'currentColor',
            },
          }}
        />
      )}
    </Stack>
  );
};
